import type { Week } from '@/lib/types';

/**
 * 주차 계산. v1 `lib/utils.ts` 를 옮긴 것으로 규칙은 그대로다 —
 * 이 규칙이 바뀌면 v1 과 v2 가 같은 날짜를 다른 주차로 부르게 되고,
 * 공존 기간 동안 두 화면의 회의록이 어긋난다.
 */

/**
 * 월요일 시작 주차가 어느 월의 몇 주차인지 계산한다.
 *
 * 규칙:
 * - 직후 월 1일이 월/화/수/목이면, 그 1일이 포함된 주를 직후 월 1주차로 본다.
 * - 직후 월 1일이 금/토/일이면, 그 주는 직전 월 마지막 주차로 보고 다음 주를 직후 월 1주차로 본다.
 */
export function getWeekLabelInfo(date: Date): { year: number; month: number; weekNumber: number } {
  const monday = getMondayOfWeek(date);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(0, 0, 0, 0);

  let labelYear = monday.getFullYear();
  let labelMonth = monday.getMonth() + 1;

  if (monday.getMonth() !== sunday.getMonth() || monday.getFullYear() !== sunday.getFullYear()) {
    const nextMonthFirst = new Date(sunday.getFullYear(), sunday.getMonth(), 1);
    const nextMonthFirstDay = nextMonthFirst.getDay();
    const isNextMonthWeekOne = nextMonthFirstDay >= 1 && nextMonthFirstDay <= 4;

    if (isNextMonthWeekOne) {
      labelYear = nextMonthFirst.getFullYear();
      labelMonth = nextMonthFirst.getMonth() + 1;
    }
  }

  const firstDayOfLabelMonth = new Date(labelYear, labelMonth - 1, 1);
  const firstDayWeekday = firstDayOfLabelMonth.getDay();
  const firstWeekMonday = new Date(firstDayOfLabelMonth);

  if (firstDayWeekday >= 1 && firstDayWeekday <= 4) {
    const diffToMonday = 1 - firstDayWeekday;
    firstWeekMonday.setDate(firstDayOfLabelMonth.getDate() + diffToMonday);
  } else if (firstDayWeekday === 0) {
    firstWeekMonday.setDate(firstDayOfLabelMonth.getDate() + 1);
  } else {
    firstWeekMonday.setDate(firstDayOfLabelMonth.getDate() + (8 - firstDayWeekday));
  }

  firstWeekMonday.setHours(0, 0, 0, 0);

  const diffWeeks = Math.floor((monday.getTime() - firstWeekMonday.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return {
    year: labelYear,
    month: labelMonth,
    weekNumber: diffWeeks + 1,
  };
}

export function getWeekOfMonth(date: Date): number {
  return getWeekLabelInfo(date).weekNumber;
}

/** 주어진 날짜가 속한 주의 월요일. */
export function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** `YYYY-MM-DD`. 서버로 보내는 형식. */
export function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** `YY.MM.DD`. 화면 표시용. */
export function formatDateKR(dateStr: string): string {
  const d = new Date(dateStr);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

/** 예: `4월 2주차` */
export function getWeekLabel(week: Pick<Week, 'startDate'>): string {
  const weekInfo = getWeekLabelInfo(new Date(`${week.startDate}T00:00:00`));
  return `${weekInfo.month}월 ${weekInfo.weekNumber}주차`;
}

/**
 * 오늘이 든 주차.
 *
 * 서버가 오늘 둘레의 주차를 채우지만, 응답이 아직 오지 않았거나 목록이 좁을 수 있다.
 * 그때 빈손으로 돌려주면 화면이 통째로 비므로 가장 최근 주차로 대신한다 —
 * `useWeeks()` 가 최근 주차를 앞에 두므로 첫 항목이 그것이다.
 */
export function findCurrentWeek<T extends Pick<Week, 'startDate' | 'endDate'>>(weeks: T[]): T | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const containing = weeks.find(
    (week) => parseWeekDate(week.startDate) <= today && today <= parseWeekDate(week.endDate),
  );

  return containing ?? weeks[0] ?? null;
}

function parseWeekDate(value: string): Date {
  const date = new Date(`${value}T00:00:00`);
  date.setHours(0, 0, 0, 0);
  return date;
}

/** 예: `4월 2주차 (26.04.06~04.12)` */
export function getWeekFullLabel(week: Week): string {
  return `${getWeekLabel(week)} (${formatDateKR(week.startDate)}~${formatDateKR(week.endDate)})`;
}

/** 한국어 자모 정렬. 영문으로 시작하는 항목은 뒤로 보낸다. */
export function koreanSort(a: string, b: string): number {
  const aIsKorean = /^[가-힣]/.test(a);
  const bIsKorean = /^[가-힣]/.test(b);
  if (aIsKorean && !bIsKorean) return -1;
  if (!aIsKorean && bIsKorean) return 1;
  return a.localeCompare(b, 'ko');
}
